import { memo, useCallback, useMemo, useState } from 'react';
import { IconSended } from '@app/icons';
import { createReview } from 'api/reviews';
import { useForm } from '@app/forms';
import { object, string } from 'yup';
import { Review } from '@app/models';
import { Modal, ModalProps, TextField } from '@app/components';
import { Button } from 'components/ui/button';
import { useTranslation } from 'react-i18next';
import { useNotification, useUserContext } from '@app/providers';
import Loader from 'components/Loader';

interface Props extends Partial<ModalProps> {
  userId?: string;
  companyId?: string;
  vehicleId?: string;
  title?: string;
  text?: string;
  getData?: () => void;
}

interface FormData {
  message: string;
}

function ModalReview(props: Props) {
  const {
    title,
    text,
    visible,
    onClose,
    userId,
    companyId,
    vehicleId,
    getData,
  } = props;
  const { company } = useUserContext();
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [succeeded, setSucceeded] = useState(false);
  const [pending, setPending] = useState(false);

  const { errors, onChange, values, validate, setErrors } = useForm<FormData>({
    message: '',
  });

  const modalTitle = useMemo(() => {
    if (succeeded) {
      return ' ';
    }
    return title || 'Оставить отзыв';
  }, [succeeded, title]);

  const onClickStar = useCallback(
    (index: number) => () => {
      setRating(index);
    },
    []
  );

  const onMouseEnter = useCallback(
    (index: number) => () => {
      setHover(index);
    },
    []
  );

  const onMouseLeave = useCallback(() => {
    setHover(rating);
  }, [rating]);

  const onSubmitForm = useCallback(async () => {
    try {
      const hasErrors = await validate(
        object().shape({
          message: string().required(t('required', { ns: 'validation' })!),
        })
      );
      if (hasErrors) {
        return;
      }
      if (rating < 1) {
        setErrors({
          message: 'Укажите рейтинг',
        });
        return;
      } else {
        setErrors({});
      }
      setPending(true);
      const data: Partial<Review> = {
        description: values.message,
        rating,
      };
      if (!!companyId) {
        data.companyId = companyId;
      }
      if (!!userId) {
        data.userId = userId;
      }
      if (!!vehicleId) {
        data.vehicleId = vehicleId;
      }
      if (!!company) {
        data.authorCompanyId = company.id;
      }
      await createReview(data);
      getData && getData();
      setSucceeded(true);
      setPending(false);
    } catch (e) {
      setSucceeded(false);
      setPending(false);
      showNotification({
        variant: 'error',
        message: 'Не удалось добавить отзыв',
      });
    }
  }, [
    company,
    companyId,
    getData,
    rating,
    setErrors,
    showNotification,
    t,
    userId,
    validate,
    values.message,
    vehicleId,
  ]);

  const onCloseModal = useCallback(() => {
    setSucceeded(false);
    onChange('', 'message');
    setRating(0);
    if (!!onClose) {
      onClose();
    }
  }, [onChange, onClose]);

  if (!visible) {
    return null;
  }

  return (
    <Modal visible={visible} onClose={onCloseModal} title={modalTitle}>
      {succeeded ? (
        <div className="flex flex-col items-center justify-center py-12">
          <IconSended className="mb-4" />
          <p className="font-semibold text-2xl leading-8 text-center mb-2">
            Отзыв отправлен!
          </p>
        </div>
      ) : (
        <>
          {!!text && (
            <p className="font-normal text-base leading-6 text-center mb-6">
              {text}
            </p>
          )}
          <div className="flex gap-4 justify-center mb-6">
            {[...Array(5)].map((star, index) => {
              index += 1;
              return (
                <button
                  key={index}
                  type="button"
                  className="bg-transparent border-none p-0 m-0 cursor-pointer text-primary text-4xl"
                  onClick={onClickStar(index)}
                  onMouseEnter={onMouseEnter(index)}
                  onMouseLeave={onMouseLeave}
                >
                  <span>{index <= (hover || rating) ? '★' : '☆'}</span>
                </button>
              );
            })}
          </div>
          <TextField
            label="комментарий"
            value={values.message}
            placeholder="Введите комментарий"
            name="message"
            onChange={onChange}
            error={!!errors.message}
            helperText={errors.message}
            editable={!pending}
          />
          <div className="flex justify-end mt-6">
            <Button onClick={onSubmitForm} disabled={pending}>
              {pending && (
                <span className="mr-2">
                  <Loader size="small" color="default" />
                </span>
              )}
              Отправить
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default memo(ModalReview);
